import { FloatButton } from "antd";
import { useFetchAllMfaDevicesQuery, useFetchMeQuery } from "features/auth/authAPI";
import useUserType from "features/auth/hooks/useUserType";
import {
  FloatingCallPopover,
  IncomingCallModal,
  InternalDialerProvider,
} from "features/internalDialer";
import { InternalMessagingProvider, MomentProvider } from "providers";
import AppearanceProvider from "providers/appearanceProvider";
import RemindersProvider from "providers/remindersProvider";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

function getRoutes(routes) {
  return routes.map((route) => {
    return <Route key={route.path} {...route} />;
  });
}

// Agents need a pickup affordance for inbound calls on non-debtor
// pages too. The debtor detail page already renders FloatingCallPopover
// inside its own FloatButton.Group (combined with SendMessagePopover),
// so we skip this global fallback there to avoid two phone buttons
// competing for the same spot. Everywhere else in the agency portal
// this gives the user a globally mounted widget that auto-opens the
// popover when a callInvite arrives (the hook in FloatingCallPopover
// already does this for any non-idle callState).
function GlobalPhoneWidgetFallback() {
  const location = useLocation();
  const isDebtorDetailRoute = /^\/debtors\/[^/]+/.test(location.pathname);
  if (isDebtorDetailRoute) return null;
  return (
    <FloatButton.Group>
      <FloatingCallPopover />
    </FloatButton.Group>
  );
}

function UserTypeRoute({ routes }) {
  const { data: me, isLoading, error } = useFetchMeQuery();
  const { isAgencyUserType } = useUserType();
  const { data: mfaDevices } = useFetchAllMfaDevicesQuery({}, { skip: !isAgencyUserType });
  const confirmedMfaDevices = mfaDevices?.filter((device) => device.confirmed);
  const navigate = useNavigate();

  if (error?.data?.error?.code === "mfa_required") {
    if (confirmedMfaDevices === undefined) {
      // navigate to previous url
      navigate(-1);
    }
    if (confirmedMfaDevices?.length > 0) {
      navigate("/otp-verification", {
        state: {
          requiredMfaDevices: confirmedMfaDevices,
        },
      });
    } else {
      navigate("/initial-setup-mfa", {
        state: {
          showTopBar: false,
        },
      });
    }
  }

  // If we are still loading the user, we don't know what routes to show
  if (isLoading || !me || !me.type) {
    return null;
  }

  const userType = me.type;

  // We are not expecting any other user types
  const byRoleRoutes = {
    agency: routes.agency,
    creditor: routes.creditor,
    developer: routes.developer,
  };

  return (
    <MomentProvider userType={userType}>
      <AppearanceProvider>
        <InternalMessagingProvider userType={userType}>
          <InternalDialerProvider>
            <RemindersProvider userType={userType}>
              <Routes>{getRoutes(byRoleRoutes[userType])}</Routes>
              <GlobalPhoneWidgetFallback />
              <IncomingCallModal />
            </RemindersProvider>
          </InternalDialerProvider>
        </InternalMessagingProvider>
      </AppearanceProvider>
    </MomentProvider>
  );
}

export default UserTypeRoute;
